import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  FileText, 
  Upload, 
  Check, 
  CheckCircle2, 
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const TicketModal = ({
  isOpen,
  onClose,
  dayNumber,
  dayEvents = [],
  assignedTickets = [],
  onSave,
  onRemove,
  currentTicket = null
}) => {
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [name, setName] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [url, setUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const resetForm = () => {
    setEditingTicketId(null);
    setName('');
    setConfirmation('');
    setUrl('');
    setFileName('');
    setSelectedEventIds([]);
  };

  const loadTicketIntoForm = (t) => {
    if (!t) {
      resetForm();
      return;
    }
    setEditingTicketId(t.id || t._id);
    setName(t.name || '');
    setConfirmation(t.confirmation || t.pnr || '');
    setUrl(t.url || '');
    setFileName(t.file_name || t.filename || t.name || '');
    setSelectedEventIds(Array.isArray(t.assigned_events) ? t.assigned_events : (t.event_id ? [t.event_id] : []));
    setSuccessMessage(null);
  };

  useEffect(() => {
    if (isOpen) {
      if (currentTicket) {
        loadTicketIntoForm(currentTicket);
      } else {
        resetForm();
      }
      setSuccessMessage(null);
    }
  }, [isOpen, currentTicket, dayNumber]);

  if (!isOpen) return null;

  const toggleEventSelection = (eventId) => {
    setSelectedEventIds(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const selectAllEvents = () => {
    if (selectedEventIds.length === dayEvents.length) {
      setSelectedEventIds([]);
    } else {
      setSelectedEventIds(dayEvents.map(e => e.id || e._id));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    setFileName(file.name);
    if (!name) {
      setName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Ticket name is required.');
      return;
    }

    const assignedTitles = dayEvents
      .filter(ev => selectedEventIds.includes(ev.id || ev._id))
      .map(ev => ev.title || ev.activity || 'Event');

    const ticketObj = {
      id: editingTicketId || `tkt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: name.trim(),
      confirmation: confirmation.trim(),
      url: url.trim(),
      file_name: fileName || name.trim(),
      day_number: dayNumber,
      assigned_events: selectedEventIds,
      assigned_event_titles: assignedTitles
    };

    onSave(ticketObj, Boolean(editingTicketId));

    const msg = `Ticket "${name.trim()}" saved.`;
    toast.success(msg);
    resetForm();
    onClose();
  };

  const handleRemove = (ticketId) => {
    onRemove(ticketId);
    if (editingTicketId === ticketId) {
      resetForm();
    }
    setSuccessMessage('Ticket removed.');
    toast.success('Ticket removed.');
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-xl shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3.5rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed at top of modal */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-100 bg-white shrink-0">
          <div>
            <span className="text-[11px] font-medium text-slate-400 block">
              Day {dayNumber} Tickets &amp; Passes
            </span>
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 tracking-tight">
              {editingTicketId ? 'Edit Ticket' : 'Attach Tickets & Passes'}
            </h3>
            <p className="text-xs text-slate-500 font-normal mt-0.5">
              Attach tickets or travel documents to Day {dayNumber} and assign to one or more events
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Body: Form on TOP, Assigned Tickets at BOTTOM */}
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* Success Banner */}
          {successMessage && (
            <div className="flex items-center justify-between gap-2 p-2.5 bg-emerald-50 border border-emerald-200/80 rounded-xl text-xs text-emerald-800 font-medium">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                <span>{successMessage}</span>
              </div>
              <button 
                type="button" 
                onClick={() => setSuccessMessage(null)}
                className="text-emerald-600 hover:text-emerald-800 text-xs px-1 cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Ticket Name & Confirmation Code */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Ticket / Activity Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Scuba Diving Entry Pass"
                  className="w-full px-3 h-[38px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Confirmation / PNR / Ticket #
                </label>
                <input
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="e.g. PNR-987654"
                  className="w-full px-3 h-[38px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs font-mono"
                />
              </div>
            </div>

            {/* Event Assignment Checkboxes */}
            <div className="space-y-2 pt-1 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-[11px] font-medium text-slate-600">
                  Assign to Events on Day {dayNumber}
                </label>
                {dayEvents.length > 1 && (
                  <button
                    type="button"
                    onClick={selectAllEvents}
                    className="text-xs text-[#067978] hover:underline cursor-pointer font-medium"
                  >
                    {selectedEventIds.length === dayEvents.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {dayEvents.length > 0 ? (
                <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                  {dayEvents.map((evt, idx) => {
                    const evtId = evt.id || evt._id || `evt_${dayNumber}_${idx + 1}`;
                    const isChecked = selectedEventIds.includes(evtId);
                    return (
                      <label
                        key={evtId}
                        className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                          isChecked 
                            ? 'bg-teal-50/60 border-teal-200 text-teal-900 font-medium' 
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleEventSelection(evtId)}
                          className="rounded border-slate-300 text-[#067978] focus:ring-[#067978] h-3.5 w-3.5"
                        />
                        <span className="text-[11px] font-medium text-slate-400 w-4">#{idx + 1}</span>
                        <span className="truncate flex-1">
                          {evt.title || evt.activity || `Event ${idx + 1}`}
                          {evt.time ? ` (${evt.time})` : ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-400 font-normal">
                  No specific events on Day {dayNumber}. This ticket will apply to the entire day.
                </p>
              )}
            </div>

            {/* Document File / URL */}
            <div className="space-y-2 pt-1 border-t border-slate-100">
              <label className="block text-[11px] font-medium text-slate-600">
                Ticket Document (PDF / Image / Link)
              </label>

              <div className="space-y-2">
                <label className="flex items-center justify-center gap-2 p-2.5 border border-dashed border-slate-200 hover:border-[#067978] rounded-xl bg-slate-50/50 hover:bg-teal-50/20 cursor-pointer transition-all">
                  <Upload size={13} className="text-[#067978]" />
                  <span className="text-xs font-medium text-[#067978]">
                    {fileName ? 'Replace File' : 'Upload Ticket PDF or Image'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>

                {fileName && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-medium border border-emerald-200">
                    <FileText size={13} className="text-emerald-600 shrink-0" />
                    <span className="truncate flex-1">{fileName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFileName('');
                        setUrl('');
                      }}
                      className="text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Or paste external ticket document URL (e.g. S3 link, Google Drive)"
                  className="w-full px-3 h-[38px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
                />
              </div>
            </div>

            {/* Form Submit Bar */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
              <div>
                {editingTicketId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer"
                  >
                    Cancel edit
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                >
                  Done
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                >
                  <Check size={13} />
                  <span>{editingTicketId ? 'Save Ticket' : 'Save / Attach'}</span>
                </button>
              </div>
            </div>
          </form>

          {/* ASSIGNED TICKETS AT THE BOTTOM */}
          {assignedTickets.length > 0 && (
            <div className="pt-4 border-t border-slate-100 space-y-2">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                Assigned Tickets for Day {dayNumber} ({assignedTickets.length})
              </div>

              <div className="divide-y divide-slate-100">
                {assignedTickets.map((t) => {
                  const eventTitles = t.assigned_event_titles || [];
                  return (
                    <div
                      key={t.id || t._id}
                      className="py-2.5 flex items-start justify-between gap-3 text-xs"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {t.name}
                          {t.confirmation && (
                            <span className="text-slate-400 font-mono font-normal"> · #{t.confirmation}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                          {eventTitles.length > 0 ? eventTitles.join(', ') : `Day ${dayNumber} Pass`}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0 pt-0.5 text-[11px]">
                        {t.url && (
                          <>
                            <a
                              href={t.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-[#067978] hover:underline inline-flex items-center gap-1"
                              title="View Document"
                            >
                              <ExternalLink size={11} /> View
                            </a>
                            <span className="text-slate-300">·</span>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => loadTicketIntoForm(t)}
                          className="text-[#067978] hover:underline cursor-pointer font-medium"
                        >
                          Edit
                        </button>
                        <span className="text-slate-300">·</span>
                        <button
                          type="button"
                          onClick={() => handleRemove(t.id || t._id)}
                          className="text-slate-400 hover:text-rose-600 hover:underline cursor-pointer font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TicketModal;
